import React from 'react';
import Typography from 'Components/DesignSystem/Typography/Typography';

export type TStatusIndicator = {
    className?: string;
    message?: string;
    placeholder?: string;
    'data-testid'?: string;
};

/**
 * The two-part "LABEL: value" strip — "RECOMMENDED ACTION Too early to recommend" above
 * the results table, and "METRIC: Revenue" above the model list (§ 154).
 *
 * The green rail on the left is the ONLY thing distinguishing a recommendation from a
 * metric caption, and it was keyed off `placeholder === "Recommended Action"` — a
 * string comparison against the default value of a prop. So a caller passing
 * `placeholder="Recommended action"` (lower-case a) silently lost the rail. It is an
 * explicit `variant` now; the string test is gone.
 *
 * Both halves were `<label>` elements with no `htmlFor`. The colours were
 * `#F4F5F7` / `#6D788C` / `#172B4D` / `#36B37E`, all of which are design-system values
 * written as hex — they are the tokens now, so they follow the theme.
 */
const StatusIndicator = ({ className, message, placeholder = "Recommended Action", 'data-testid': dataTestId }: TStatusIndicator) => {
    /* Preserves exactly the old behaviour, including the case-sensitivity, so no
       caller's rendering changes — but the rule is now readable at a glance. */
    const showRail = placeholder === "Recommended Action";

    return (
        <div
            className={`tw-rounded-ctl tw-bg-surface-sunken tw-w-auto tw-flex tw-items-center tw-justify-start tw-pr-3 tw-box-border tw-gap-1.5 tw-overflow-hidden ${className ?? ''}`}
            data-testid={dataTestId}
        >
            {/* The rail is decorative — the words beside it carry the meaning. */}
            <div
                aria-hidden="true"
                className={`tw-h-8 tw-w-[5px] tw-shrink-0 ${showRail ? 'tw-bg-tone-success-bold' : ''}`}
            />
            <div className="tw-flex tw-items-center tw-ps-1.5">
                <Typography variant="caption-metadata" as="span" className="tw-uppercase tw-text-ink-tertiary">
                    {placeholder}
                </Typography>
            </div>
            <div className="tw-flex tw-items-center">
                <Typography
                    variant="sub-title-label"
                    as="span"
                    className="tw-text-ink"
                    data-testid={dataTestId ? `${dataTestId}-message` : undefined}
                >
                    {message ?? 'Run either Control or Variation 1 in 100% traffic'}
                </Typography>
            </div>
        </div>
    );
}

export default StatusIndicator;
